/**
 * Sticky Add to Cart – Mobile Only (max-width: 767px)
 *
 * - IntersectionObserver detects when the original buy-button leaves viewport
 * - Click delegates to the native Shopify submit button (keeps all form logic)
 * - MutationObserver (subtree) + pubsub variant-change keep state in sync
 */
class StickyAddToCart extends HTMLElement {
  connectedCallback() {
    if (window.innerWidth > 767) return;

    this._sectionId = this.dataset.section;

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this._init(), {
        once: true,
      });
    } else {
      setTimeout(() => this._init(), 0);
    }
  }

  _init() {
    this._anchor = document.getElementById(
      `buy-buttons-original-${this._sectionId}`,
    );
    this._stickyBtn = this.querySelector(".sticky-atc__btn");
    this._originalBtn = document.getElementById(
      `ProductSubmitButton-${this._sectionId}`,
    );

    if (!this._anchor || !this._stickyBtn || !this._originalBtn) {
      if (this._anchor) {
        this._originalBtn = this._anchor.querySelector(
          '[type="submit"][name="add"]',
        );
      }
      if (!this._anchor || !this._stickyBtn || !this._originalBtn) return;
    }

    this._setupObserver();
    this._setupClickDelegation();
    this._setupVariantSync();
    this._syncState();
  }

  _setupObserver() {
    this._io = new IntersectionObserver(
      (entries) => {
        this._setVisible(!entries[0].isIntersecting);
      },
      { threshold: 0, rootMargin: "0px" },
    );
    this._io.observe(this._anchor);
  }

  _setupClickDelegation() {
    this._stickyBtn.addEventListener("click", (e) => {
      e.preventDefault();
      if (this._stickyBtn.getAttribute("aria-disabled") === "true") return;
      this._originalBtn.click();
    });
  }

  _setupVariantSync() {
    this._mo = new MutationObserver(() => {
      requestAnimationFrame(() => this._syncState());
    });
    this._mo.observe(this._originalBtn, {
      attributes: true,
      attributeFilter: ["disabled", "aria-disabled", "class"],
      childList: true,
      subtree: true,
      characterData: true,
    });

    const listenPubSub = () => {
      if (typeof subscribe === "function") {
        this._unsubscribe = subscribe("variant-change", () => {
          setTimeout(() => this._syncState(), 50);
        });
        return true;
      }
      return false;
    };

    if (!listenPubSub()) {
      window.addEventListener("load", listenPubSub, { once: true });
    }
  }

  _syncState() {
    if (!this._originalBtn || !this._stickyBtn) return;

    const isDisabled =
      this._originalBtn.disabled ||
      this._originalBtn.getAttribute("aria-disabled") === "true";

    if (isDisabled) {
      this._stickyBtn.setAttribute("aria-disabled", "true");
      this._stickyBtn.disabled = true;
    } else {
      this._stickyBtn.removeAttribute("aria-disabled");
      this._stickyBtn.disabled = false;
    }

    if (this._originalBtn.classList.contains("loading")) {
      this._stickyBtn.classList.add("loading");
      this._stickyBtn
        .querySelector(".loading__spinner")
        ?.classList.remove("hidden");
    } else {
      this._stickyBtn.classList.remove("loading");
      this._stickyBtn
        .querySelector(".loading__spinner")
        ?.classList.add("hidden");
    }

    const srcSpan = [...this._originalBtn.children].find(
      (el) =>
        el.tagName === "SPAN" &&
        !el.classList.contains("loading__spinner") &&
        !el.classList.contains("visually-hidden"),
    );
    const dstSpan = [...this._stickyBtn.children].find(
      (el) =>
        el.tagName === "SPAN" &&
        !el.classList.contains("loading__spinner") &&
        !el.classList.contains("visually-hidden"),
    );

    if (srcSpan && dstSpan) {
      const srcText = srcSpan.textContent.trim();
      if (srcText && dstSpan.textContent.trim() !== srcText) {
        dstSpan.textContent = srcText;
      }
    }
  }

  _setVisible(show) {
    if (show) {
      this.classList.add("sticky-atc--visible");
      this.removeAttribute("aria-hidden");
    } else {
      this.classList.remove("sticky-atc--visible");
      this.setAttribute("aria-hidden", "true");
    }

    try {
      this._adjustBackToTop(show);
    } catch (e) {
      console.error(e);
    }
  }

  _adjustBackToTop(show) {
    if (window.innerWidth > 767) return;

    const backBtn = document.getElementById("BackToTop");
    if (!backBtn) return;

    if (show) {
      const rect = this.getBoundingClientRect();
      const gap = 12;
      const newBottom = Math.ceil(rect.height + gap) + "px";
      backBtn.style.bottom = newBottom;
    } else {
      backBtn.style.bottom = "";
    }
  }

  disconnectedCallback() {
    this._io?.disconnect();
    this._mo?.disconnect();
    this._unsubscribe?.();
  }
}

if (!customElements.get("sticky-add-to-cart")) {
  customElements.define("sticky-add-to-cart", StickyAddToCart);
}
